import { ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import * as api from 'api/api'
import { Project, Report, ReportListItem, Workflow } from 'api/types'
import { useAuth } from 'auth/AuthContext'
import { Icon, IconName } from 'components/Icon'
import { useProject } from 'components/Layout'
import { ReturnedBanner } from 'components/reports/ReturnedBanner'
import { Button, Card, EmptyState, ErrorState, ProgressBar, Skeleton, Spinner, StatusBadge } from 'components/ui'
import { SECTION_LABELS, WEEK_DAYS, WORKFLOW_LABELS, addDays, formatDate, formatDateTime, parseIso } from 'lib/format'
import { useAsync } from 'lib/hooks'
import { t } from 'lib/i18n'

import { contentOf, normalize, sectionFillStates, submitChecklist } from './report/checklist'

const weekdayFromDate = (iso: string) => WEEK_DAYS[(parseIso(iso).getUTCDay() + 1) % 7]

const WORKFLOW_BADGE_CLASS: Record<Workflow, string> = {
  APPROVED: 'badge badge--complete',
  DRAFT: 'badge badge--draft',
  EMPTY: 'badge badge--new',
  RETURNED: 'badge badge--missing',
  SUBMITTED: 'badge badge--created',
}

const TodaySkeleton = () => (
  <div className="page" aria-busy="true">
    <div className="dashboard-skeleton__greeting">
      <Skeleton width={160} height={16} />
      <Skeleton width={220} height={16} />
    </div>
    <div className="dashboard-skeleton__stats">
      {[0, 1, 2].map((index) => (
        <div className="dashboard-skeleton__stat" key={index}>
          <Skeleton width={96} height={12} />
          <Skeleton width={64} height={28} />
        </div>
      ))}
    </div>
    <Skeleton height={140} />
  </div>
)

/** What the recent-reports row shows under the code: when it happened, in the engineer's own words. */
const recentDetail = (item: ReportListItem) => {
  if (item.workflow === 'RETURNED')
    return item.notesCount
      ? t(`${item.notesCount} ملاحظة إشراف`, `${item.notesCount} supervision notes`)
      : t('تقرير للمراجعة', 'Report for review')
  if (item.workflow === 'APPROVED') return t('معتمد', 'Approved')
  if (item.workflow === 'SUBMITTED') return t('بانتظار الاعتماد', 'Waiting for approval')
  return t(`${item.filledSections.length} من 7 أقسام`, `${item.filledSections.length} of 7 sections`)
}

const RecentReportRow = ({ projectId, item }: { projectId: string; item: ReportListItem }) => (
  <li className="today-recent__row">
    <span className="today-icon">
      <Icon name="file" size={16} />
    </span>
    <span className="today-recent__body">
      <Link to={`/projects/${projectId}/reports/${item.id}`} className="code-link">
        {item.code}
      </Link>
      <span className="muted small">
        {formatDate(item.reportDate)} · {recentDetail(item)}
      </span>
    </span>
    <span className={WORKFLOW_BADGE_CLASS[item.workflow]}>{WORKFLOW_LABELS[item.workflow]}</span>
  </li>
)

const RecentReportsCard = ({
  project,
  items,
  loading,
  error,
  reload,
}: {
  project: Project
  items: ReportListItem[]
  loading: boolean
  error: string | null
  reload: () => void
}) => (
  <Card
    title={t('أحدث التقارير', 'Recent reports')}
    actions={
      <Link to={`/projects/${project.id}`} className="link-button">
        {t('عرض الكل', 'View all')}
      </Link>
    }
  >
    {loading && !items.length ? (
      <Spinner />
    ) : error ? (
      <ErrorState message={error} onRetry={reload} />
    ) : !items.length ? (
      <EmptyState icon="file" title={t('لا توجد تقارير سابقة', 'No earlier reports')} />
    ) : (
      <ul className="today-recent">
        {items.map((item) => (
          <RecentReportRow key={item.id} projectId={project.id} item={item} />
        ))}
      </ul>
    )}
  </Card>
)

interface TodayTask {
  id: string
  icon: IconName
  title: string
  meta: ReactNode
  done: boolean
  statusLabel: string
  statusClass: string
  to: string
}

/** Today's report progress, requirements and quick actions — only rendered once the report is loaded. */
const TodayReportSection = ({
  project,
  report,
  returnedReports,
}: {
  project: Project
  report: Report
  returnedReports: ReportListItem[]
}) => {
  const navigate = useNavigate()
  const normalized = normalize(contentOf(report))
  const sections = sectionFillStates(normalized, report.notes)
  const filledCount = sections.filter((item) => item.filled).length
  const missingLabels = sections.filter((item) => !item.filled).map((item) => SECTION_LABELS[item.section])
  const checklist = submitChecklist(normalized, report.attachments)
  const pendingCount = checklist.filter((item) => !item.done).length
  const staffTotal = report.staff.reduce((sum, line) => sum + line.count, 0)
  const equipmentTotal = report.equipment.reduce((sum, line) => sum + line.count, 0)
  const reportPath = `/projects/${project.id}/reports/${report.id}`
  const goToReport = () => navigate(reportPath)

  const tasks: TodayTask[] = [
    {
      done: !report.permissions.canEdit,
      icon: 'file',
      id: 'today-report',
      meta: `${report.code} · ${t(`${filledCount} من ${sections.length} أقسام`, `${filledCount} of ${sections.length} sections`)}`,
      statusClass: report.permissions.canEdit ? 'chip chip--info' : WORKFLOW_BADGE_CLASS[report.workflow],
      statusLabel: report.permissions.canEdit ? t('قيد التنفيذ', 'In progress') : WORKFLOW_LABELS[report.workflow],
      title: t('إكمال تقرير اليوم', "Finish today's report"),
      to: reportPath,
    },
    ...returnedReports.map((item) => ({
      done: false,
      icon: 'undo' as IconName,
      id: `fix-${item.id}`,
      meta: `${item.code} · ${item.notesCount ? t(`${item.notesCount} ملاحظة إشراف`, `${item.notesCount} supervision notes`) : t('تقرير للمراجعة', 'Report for review')}`,
      statusClass: 'badge badge--missing',
      statusLabel: t('تقرير للمراجعة', 'Report for review'),
      title: t('تقرير للمراجعة', 'Report for review'),
      to: `/projects/${project.id}/reports/${item.id}`,
    })),
  ]
  const doneTasks = tasks.filter((task) => task.done).length

  return (
    <>
      <ReturnedBanner report={report} />

      {report.permissions.canEdit && report.workflow !== 'RETURNED' ? (
        <div className="alert alert--warning" role="status">
          <Icon name="alert" />
          <div>
            <strong>{t('لم يُرسل تقرير اليوم بعد', "Today's report has not been submitted")}</strong>
            <p>
              {t(`${filledCount} من ${sections.length} أقسام معبأة`, `${filledCount} of ${sections.length} sections filled`)}
              {missingLabels.length ? ` · ${t('لا يزال يجب إكمال', 'Still to complete')}: ${missingLabels.join(t('، ', ', '))}` : ''}
            </p>
          </div>
          <div style={{ alignSelf: 'center', marginInlineStart: 'auto' }}>
            <Button variant="primary" size="sm" icon="edit" onClick={goToReport}>
              {t('متابعة التقرير', 'Continue the report')}
            </Button>
          </div>
        </div>
      ) : null}

      <div className="stats stats--static" role="group" aria-label={t('إحصائيات تقرير اليوم', "Today's report stats")}>
        <div className="stat">
          <span className="stat__value">{staffTotal}</span>
          <span className="stat__label">{t('الكوادر اليوم', 'Crew today')}</span>
        </div>
        <div className="stat">
          <span className="stat__value">{equipmentTotal}</span>
          <span className="stat__label">{t('المعدات في الموقع', 'Equipment on site')}</span>
        </div>
        <div className="stat">
          <span className="stat__value">{pendingCount}</span>
          <span className="stat__label">{t('عناصر تحتاج إكمال', 'Items still to complete')}</span>
        </div>
      </div>

      <Card>
        <div className="today-continue__head">
          <span className="today-icon">
            <Icon name="file" size={18} />
          </span>
          <div className="today-continue__text">
            <span className="today-continue__heading">{t('متابعة تقرير اليوم', "Continue today's report")}</span>
            <span className="muted small">
              {report.code} · {formatDate(report.reportDate)} · {weekdayFromDate(report.reportDate)}
            </span>
          </div>
          <div style={{ marginInlineStart: 'auto' }}>
            <StatusBadge status={report.status} />
          </div>
        </div>

        <p className="today-continue__progress-label">
          {t(`${filledCount} من ${sections.length} أقسام مكتملة`, `${filledCount} of ${sections.length} sections complete`)}
        </p>
        <div className="fill">
          <ProgressBar value={filledCount / Math.max(sections.length, 1)} label={t(`نسبة اكتمال ${report.code}`, `Completion for ${report.code}`)} />
          <span className="num">
            {filledCount}/{sections.length}
          </span>
        </div>
        {missingLabels.length ? (
          <p className="muted small">
            {t('لا يزال يجب إكمال', 'Still to complete')}: {missingLabels.join(t('، ', ', '))}
          </p>
        ) : null}

        <div className="today-continue__footer">
          <span className="muted small">
            {report.lastEditedAt ? (
              <>
                <Icon name="check" size={14} /> {t('محفوظ', 'Saved')} · {formatDateTime(report.lastEditedAt)}
              </>
            ) : (
              t('لم يُحفظ بعد', 'Not saved yet')
            )}
          </span>
          <Button variant="primary" icon={report.permissions.canEdit ? 'edit' : 'eye'} onClick={goToReport}>
            {report.permissions.canEdit ? t('متابعة التقرير', 'Continue the report') : t('عرض التقرير', 'View report')}
          </Button>
        </div>
      </Card>

      <Card title={t('مهام اليوم', "Today's tasks")} actions={<span className="muted small">{t(`${doneTasks} من ${tasks.length} منجز`, `${doneTasks} of ${tasks.length} done`)}</span>}>
        <ul className="today-tasks">
          {tasks.map((task) => (
            <li key={task.id} className={task.done ? 'today-tasks__row is-done' : 'today-tasks__row'}>
              <span className="today-tasks__status" aria-hidden="true">
                {task.done ? <Icon name="check" size={12} /> : null}
              </span>
              <span className="today-tasks__body">
                <Link to={task.to} className="today-tasks__title">
                  <Icon name={task.icon} size={14} /> {task.title}
                </Link>
                <span className="muted small">{task.meta}</span>
              </span>
              <span className={task.statusClass}>{task.statusLabel}</span>
            </li>
          ))}
        </ul>
      </Card>

      {report.permissions.canEdit ? (
        <Card title={t('متطلبات إرسال التقرير', 'Submit requirements')}>
          <ul className="checklist">
            {checklist.map((item) => (
              <li key={item.label} className={item.done ? 'is-done' : 'is-todo'}>
                <Icon name={item.done ? 'check' : 'alert'} size={16} />
                <span>{t(item.label, item.labelEn)}</span>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </>
  )
}

/** Site-engineer landing page: today's report progress plus recent reports (Figma: Site Engineer / 01 · Engineer Today). */
export const TodayPage = () => {
  const { project } = useProject()
  const { user } = useAuth()

  const { data: today } = useAsync(api.fetchToday, [])
  const inRange = Boolean(today && today >= project.startDate && today <= project.endDate)

  const {
    data: reportId,
    loading: idLoading,
    error: idError,
    reload: reloadId,
  } = useAsync(() => (today && inRange ? api.findReportByDate(project.id, today) : Promise.resolve(null)), [project.id, today, inRange])

  const {
    data: report,
    loading: reportLoading,
    error: reportError,
    reload: reloadReport,
  } = useAsync(() => (reportId ? api.fetchReport(reportId) : Promise.resolve(null)), [reportId])

  const from = today ? addDays(today, -29) : undefined
  const {
    data: recent,
    loading: recentLoading,
    error: recentError,
    reload: reloadRecent,
  } = useAsync(() => (from && today ? api.fetchReports(project.id, from, today) : Promise.resolve([])), [project.id, from, today])

  const initialLoading = !today || idLoading || (Boolean(reportId) && reportLoading && !report)
  if (initialLoading) return <TodaySkeleton />

  const firstName = user?.name?.trim().split(' ')[0] ?? ''
  const recentReports = (recent ?? [])
    .filter((item) => item.reportDate !== today)
    .sort((a, b) => (a.reportDate < b.reportDate ? 1 : -1))
    .slice(0, 6)
  const returnedReports = recentReports.filter((item) => item.workflow === 'RETURNED')

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1>{t(`مرحباً، ${firstName}`, `Hello, ${firstName}`)}</h1>
          <p className="muted">
            {today ? formatDate(today) : ''} · {project.name}
          </p>
        </div>
      </div>

      <div className="report__layout">
        <div className="report__main">
          {!inRange ? (
            <Card>
              <EmptyState icon="calendar" title={t('اليوم خارج نطاق تواريخ هذا المشروع', "Today is outside this project's dates")} />
            </Card>
          ) : idError ? (
            <ErrorState message={idError} onRetry={reloadId} />
          ) : reportError ? (
            <ErrorState message={reportError} onRetry={reloadReport} />
          ) : !report ? (
            <Spinner />
          ) : (
            <TodayReportSection project={project} report={report} returnedReports={returnedReports} />
          )}
        </div>

        <aside className="report__aside">
          <RecentReportsCard
            project={project}
            items={recentReports}
            loading={recentLoading}
            error={recentError}
            reload={reloadRecent}
          />
        </aside>
      </div>
    </div>
  )
}
